// src/pages/user/SubmissionDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AttachmentListSection from "./AttachmentListSection";
import SubmissionSectionTree from "./SubmissionSectionTree";
import { IDSBreadcrumbs, IDSButton, IDSAlert } from "@inera/ids-react";
import { formatDateTimeNoSeconds } from "../../utils/dateTime";
import { getSubmissionStatusLabel } from "../../utils/submissionStatus";
import { exportSubmissionToPdf } from "../../utils/submissionPdfExporter";
import {
  fetchE2EUserProfilesByIds,
  resolveE2EUserDisplayNameWithOrg,
} from "../../utils/e2eUserProfiles";
import { resolveReviewingDepartmentLabel } from "../../utils/e2eReviewingDepartment";
import {
  getAttachments,
  downloadAttachment,
  getUserSubmission,
} from "../../services/E2EAPI";

export default function SubmissionDetails() {
  const { submissionId } = useParams();
  const { isAuthenticated } = useAuth();

  const [submission, setSubmission] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userProfilesById, setUserProfilesById] = useState({});

  const handleExportPdf = () => {
    exportSubmissionToPdf(
      submission,
      submissionId,
      formatDateTimeNoSeconds,
      getSubmissionStatusLabel,
      userProfilesById
    );
  };

  // Fetch the submission on mount
  useEffect(() => {
    async function fetchSubmission() {
      try {
        setLoading(true);
        setError("");

        const data = await getUserSubmission(submissionId);
        setSubmission(data);

        // Fetch user profiles for displaying names
        try {
          // Only the submitting user is resolvable here; reviewers are shown as
          // a department name and never exposed as ids to the tester.
          const relatedUserIds = [data?.userId].filter(Boolean);
          if (relatedUserIds.length > 0) {
            const profiles = await fetchE2EUserProfilesByIds(relatedUserIds);
            setUserProfilesById(profiles);
          }
        } catch (profileErr) {
          console.error("Failed to load user profiles:", profileErr);
        }

        try {
          const attachmentsData = await getAttachments(submissionId, false);
          setAttachments(attachmentsData || []);
        } catch (attachmentErr) {
          console.error("Failed to load attachments", attachmentErr);
        }
      } catch (err) {
        console.error("Failed to load submission", err);
        setError("Kunde inte ladda inlämningen.");
      } finally {
        setLoading(false);
      }
    }

    if (submissionId && isAuthenticated) fetchSubmission();
  }, [submissionId, isAuthenticated]);

  if (loading) return <p className="ids-mt-4">Laddar inlämning…</p>;
  if (error) return (
    <div className="ids-m-6"><main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>
      <IDSAlert type="error"><p>{error}</p></IDSAlert>
    </main></div>
  );
  if (!submission || !submission.form)
    return <p className="ids-mt-4">Inlämningen saknar formulärdata.</p>;

  const { status, submittedAt, form } = submission;
  const { formTitle, version, description, sections = [] } = form;

  return (
    <div className="ids-m-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>

        <div className="ids-mb-6">
          <IDSBreadcrumbs lead="Du är här:">
            <Link to="/start">Start</Link>
            <Link to="/e2e/tester/submissions">Inlämnade E2E underlag</Link>
              <a>
                 Visa formulär
              </a>
          </IDSBreadcrumbs>
        </div>

        <h2 className="ids-heading-l ids-mb-6">Visa formulär</h2>

        {/* Submission metadata */}
        <div className="ids-mb-6">
          <p className="ids-mb-2"><strong>Formulär: </strong>{formTitle} v{version}</p>
          {description && <p className="ids-mb-2">{description}</p>}
          <p className="ids-mb-2"><strong>Status: </strong>{getSubmissionStatusLabel(status)}</p>
          <p className="ids-mb-2"><strong>Inskickad: </strong> 
            {submittedAt
              ? formatDateTimeNoSeconds(submittedAt)
              : "—"}
          </p>
          {submission?.userId && (
            <p className="ids-mb-2"><strong>Inlamnad av: </strong>{resolveE2EUserDisplayNameWithOrg(submission.userId, userProfilesById)}</p>
          )}
          <p className="ids-mb-2"><strong>Handläggs av: </strong>
            {resolveReviewingDepartmentLabel(submission)}
          </p>
          {status === "APPROVED" && (
            <div className="ids-mt-4">
              <IDSButton size="m" type="button" onClick={handleExportPdf}>
                Exportera PDF
              </IDSButton>
            </div>
          )}
        </div>

        <hr className="ids-mb-6" />

        {sections.length === 0 && <p>Inga sektioner hittades.</p>}
        <SubmissionSectionTree sections={sections} />

        <AttachmentListSection
          attachments={attachments}
          onDownloadAttachment={(att) =>
            downloadAttachment(submissionId, att.id, false)
          }
        />

      </main>
    </div>
  );
}